import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";

// MUI Icons
import GavelIcon from "@mui/icons-material/Gavel";
import DescriptionIcon from "@mui/icons-material/Description";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import MemoryIcon from "@mui/icons-material/Memory";
import RouterIcon from "@mui/icons-material/Router";
import SecurityIcon from "@mui/icons-material/Security";
import BalanceIcon from "@mui/icons-material/Balance";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import LaunchIcon from "@mui/icons-material/Launch";
import KeyIcon from "@mui/icons-material/Key";
import CopyrightIcon from "@mui/icons-material/Copyright";
import BrandingWatermarkIcon from "@mui/icons-material/BrandingWatermark";
import LanguageIcon from "@mui/icons-material/Language";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";

// Import sub-components
import KthPatentProsecution from "./patent-service-components/KthPatentProsecution";
import KthPatentAnalysis from "./patent-service-components/KthPatentAnalysis";
import KthPatentLicensing from "./patent-service-components/KthPatentLicensing";
import KthAiPoweredPatentAnalysis from "./patent-service-components/KthAiPoweredPatentAnalysis";
import KthPatentSpecializations from "./patent-service-components/KthPatentSpecializations";
import KthPatentLitigationSupport from "./patent-service-components/KthPatentLitigationSupport";
import KthAmazonPatentEnforcement from "./patent-service-components/KthAmazonPatentEnforcement";
import KthPatentContactSection from "./patent-service-components/KthPatentContactSection";

// Import utilities
import { getImageUrl } from "../../utils/helpers";
import SetDocumentMeta from "../common/SetDocumentMeta";
import InfoBox from "./utils/InfoBox";

// Define "Jump to Section" items with icons
const jumpToSections = [
  {
    id: "patent-drafting",
    label: "Patent Drafting & Prosecution",
    icon: <DescriptionIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "patent-analysis",
    label: "Patent Analysis & Portfolio Counseling",
    icon: <AnalyticsIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "patent-licensing",
    label: "Patent Licensing & Monetization",
    icon: <MonetizationOnIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "amazon-enforcement",
    label: "Amazon Patent Enforcement",
    icon: <ShoppingCartIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "ai-patent-analysis",
    label: "AI-Powered Patent Analysis",
    icon: <SmartToyIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "patent-specializations",
    label: "Patent Specializations",
    icon: <MemoryIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
  {
    id: "litigation-support",
    label: "Litigation Support",
    icon: <BalanceIcon sx={{ mr: 1, color: "#4ABDAC" }} />,
  },
];

const patentResourceLinks = [
  {
    id: "news-insights",
    label: "Patent News & Insights",
    icon: <DescriptionIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/news-insights?practice=patents",
  },
  {
    id: "patent-ai-resources",
    label: "Patent AI Resources",
    icon: <SmartToyIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/patent-ai-resources",
  },
  // {
  //   id: "amazon-patent",
  //   label: "Amazon Patent Enforcement",
  //   icon: <ShoppingCartIcon sx={{ mr: 1, color: "#007BFF" }} />,
  //   externalLink: "/amazon-patent-enforcement",
  // },
  {
    id: "ai-agreements",
    label: "Legal Agreements for AI Technology",
    icon: <LanguageIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/legal-agreements-for-ai",
  },
  {
    id: "trademark",
    label: "Brand Protection (Trademarks)",
    icon: <BrandingWatermarkIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/trademarks",
  },
  {
    id: "copyright",
    label: "Software Licensing (Copyrights)",
    icon: <CopyrightIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/software-licensing",
  },
  {
    id: "trade-secret",
    label: "Trade Secret Protection",
    icon: <KeyIcon sx={{ mr: 1, color: "#007BFF" }} />,
    externalLink: "/trade-secrets",
  },
  {
    id: "contact-section",
    label: "Schedule a Consultation",
    icon: <PermContactCalendarIcon sx={{ mr: 1, color: "#007BFF" }} />,
  },
];

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const KthServicesPatents = ({ baseConfig }) => {
  const {
    priColor,
    priHoverColor,
    tertColor,
    tertHoverColor,
    quadColor,
    quadHoverColor,
    secColor,
    linkColor,
  } = baseConfig.siteBranding;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Custom styles
  const sectionStyles = {
    header: {
      py: 3,
      px: isMobile ? 2 : 4,
      mb: 4,
      borderRadius: 2,
      backgroundImage: `linear-gradient(135deg, ${tertColor}22 0%, ${tertColor}44 100%)`,
      boxShadow: "0 3px 5px rgba(0, 0, 0, 0.1)",
    },
    sectionTitle: {
      px: 2,
      py: 1.5,
      mb: 2,
      borderRadius: 1,
      backgroundColor: tertColor,
      color: "white",
      fontWeight: "medium",
      display: "flex",
      alignItems: "center",
    },
    card: {
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      borderRadius: 2,
      overflow: "hidden",
      transition: "all 0.3s ease",
      "&:hover": {
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
        transform: "translateY(-2px)",
      },
    },
  };

  // SEO metadata
  SetDocumentMeta({
    title:
      "Engineering & Tech Patent Lawyer | Drafting, Prosecution & Analysis",
    description:
      "Secure your innovations with expert patent drafting, prosecution, and strategic portfolio analysis. Our experienced attorneys specialize in engineering and technology patents, including wireless, AI, high-tech electrical, software, and computer systems inventions. Protect your intellectual property and maximize its value.",
    keywords:
      "patent drafting, patent prosecution, patent analysis, patent portfolio counseling, engineering patents, technology patents, wireless patents, AI patents, ML patents, high-tech electrical patents, software patents, computer systems patents, intellectual property lawyer, patent attorney, patent licensing, utility patents, design patents, provisional patents, non-provisional patents, patent examination, patent office actions, SEP, standards essential patents",
  });

  return (
    <Container sx={{ py: 4 }} className="link-no-decoration">
      {/* ======== PAGE HEADER ======== */}
      <Box component="header" sx={sectionStyles.header}>
        {/* Banner / Headline */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            gutterBottom
            color="text.primary"
          >
            Engineering & Technology Patent Law Services
          </Typography>
          <Typography
            variant="h6"
            fontWeight="medium"
            color="text.secondary"
            gutterBottom
          >
            Expert Patent Drafting, Prosecution, Analysis & Licensing
          </Typography>
          <Divider
            sx={{ my: 2, width: "20%", mx: "auto", borderColor: tertColor }}
          />
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            mt: 3,
            lineHeight: 1.8,
            textAlign: "center",
            maxWidth: "85%",
            mx: "auto",
            fontSize: "1.1rem",
          }}
        >
          <strong>In today's fast-paced technology landscape,</strong>{" "}
          protecting your innovations is crucial to maintaining your competitive
          edge. Our firm provides comprehensive patent services tailored to
          engineering and technology companies, from startups to established
          enterprises. We combine deep technical expertise with strategic legal
          counsel to help you secure, analyze, and monetize your intellectual
          property assets.
        </Typography>
      </Box>

      {/* ======== TABLE OF CONTENTS ======== */}
      <Card variant="outlined" sx={{ ...sectionStyles.card, mb: 4 }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Typography
            variant="h5"
            gutterBottom
            fontWeight="medium"
            sx={{
              color: tertColor,
              borderBottom: `2px solid ${tertColor}`,
              pb: 1,
            }}
          >
            Quick Navigation
          </Typography>

          <Grid container spacing={3}>
            {/* Main Patent Services */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{ pt: 1 }}
              >
                Patent Services
              </Typography>
              <List dense disablePadding>
                {jumpToSections.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    <ListItemButton
                      onClick={() => scrollToSection(section.id)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: `${tertColor}22`,
                          pl: 2,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText primary={section.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Patent Resources */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                gutterBottom
                sx={{ pt: 1 }}
              >
                Patent Resources & Related Services
              </Typography>
              <List dense disablePadding>
                {patentResourceLinks.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    {section.externalLink ? (
                      <ListItemButton
                        component={RouterLink}
                        to={section.externalLink}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: `${linkColor}22`,
                            pl: 2,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {section.icon}
                        </ListItemIcon>
                        <ListItemText primary={section.label} />
                        <LaunchIcon
                          sx={{ fontSize: 16, ml: 1, color: linkColor }}
                        />
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        onClick={() => scrollToSection(section.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: `${linkColor}22`,
                            pl: 2,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {section.icon}
                        </ListItemIcon>
                        <ListItemText primary={section.label} />
                      </ListItemButton>
                    )}
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Fixed Fee Patents Info Box */}
      <InfoBox
        title="SPECIAL OFFER: Discounted Fixed Fee Patent Services"
        content={
          <Box>
            <Typography variant="body1" paragraph>
              We provide discounted fixed fee patent drafting and prosecution
              services. Our deep technical expertise enables us to prepare
              high-quality patent applications more efficiently than general
              practice firms.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Additional discounts available</strong> for multiple
              filings with overlapping subject matter, leveraging drafting
              efficiencies.
            </Typography>
            <Box display="flex" mt={2}>
              <Button
                startIcon={<PhoneInTalkIcon />}
                variant="contained"
                onClick={() => scrollToSection("contact-section")}
                sx={{
                  backgroundColor: linkColor,
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: priHoverColor,
                  },
                }}
              >
                Request Free Quote & Strategy Consultation
              </Button>
            </Box>
          </Box>
        }
        type="primary"
        icon={<GavelIcon />}
      />

      {/* Patent Drafting & Prosecution */}
      <Box id="patent-drafting" sx={{ mt: 4 }}>
        <KthPatentProsecution
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Patent Analysis & Portfolio Management */}
      <Box id="patent-analysis" sx={{ mt: 4 }}>
        <KthPatentAnalysis
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Patent Licensing & Monetization */}
      <Box id="patent-licensing" sx={{ mt: 4 }}>
        <KthPatentLicensing
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Amazon Patent Enforcement */}
      <Box id="amazon-enforcement" sx={{ mt: 4 }}>
        <KthAmazonPatentEnforcement
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* AI-Powered Patent Analysis */}
      <Box id="ai-patent-analysis" sx={{ mt: 4 }}>
        <KthAiPoweredPatentAnalysis
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Patent Specializations */}
      <Box id="patent-specializations" sx={{ mt: 4 }}>
        <KthPatentSpecializations
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Litigation Support */}
      <Box id="litigation-support" sx={{ mt: 4 }}>
        <KthPatentLitigationSupport
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Contact Section */}
      <Box id="contact-section" sx={{ mt: 4 }}>
        <KthPatentContactSection
          baseConfig={baseConfig}
          sectionStyles={sectionStyles}
        />
      </Box>

      {/* Final Disclaimer */}
      <Box
        // textAlign="center"
        mt={6}
        pt={2}
        borderTop="1px solid #ccc"
        sx={{
          backgroundColor: "#f9f9f9",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" fontStyle="italic" color="text.secondary">
          Disclaimer: Please do not share confidential details until we confirm
          our representation. This website provides general information and is
          not intended as legal advice for your specific situation.
        </Typography>
      </Box>
    </Container>
  );
};

export default KthServicesPatents;
